import 'scroll-shadow-element';
import { FC, HTMLAttributes, RefObject, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { styled, theme } from 'twin.macro';
import 'styled-components/macro';

import {
  AssignmentMarkdown,
  AssignmentMarkdownProps,
} from 'component-lib/molecules/assignment-markdown/AssignmentMarkdown';
import CompletionBanner from 'play/legacy/assignment/CompletionBanner';
import { ReactComponent as CollapseIcon } from 'play/legacy/assignment/images/collapse.svg';
import { ReactComponent as NotesIcon } from 'play/legacy/assignment/images/notes.svg';
import { ChallengeTimer } from 'play/legacy/assignment/timer/ChallengeTimer';
import { ChallengeActions } from 'play/molecules/challenge-actions/ChallengeActions';
import { Progress, ProgressProps } from 'play/organisms/progress/Progress';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { addInvitePrefix } from 'support/location';
import { ChallengeStatus, LearnEnvironmentLegacyQuery, LearnEnvironmentQuery, Mode } from 'types/graphql';

export const Top = styled.div`
  height: 50px;
  min-height: 50px;
  width: 100%;
  background-color: #313d53;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

export const TopContent = styled.div`
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  padding: 0 17px;
`;

const ShadowContainer = styled.div`
  flex-grow: 1;
  width: 100%;
  overflow: hidden;

  /* See: https://github.com/ingmarh/scroll-shadow-element#configuration */
  scroll-shadow {
    height: 100%;
    width: 100%;

    --scroll-gradient-from: rgba(0 0 0 / 30%);
    --scroll-gradient-to: transparent;

    --scroll-shadow-size: 20;
    --scroll-shadow-top: linear-gradient(to bottom, var(--scroll-gradient-from), var(--scroll-gradient-to));
    --scroll-shadow-bottom: linear-gradient(to top, var(--scroll-gradient-from), var(--scroll-gradient-to));
    --scroll-shadow-left: linear-gradient(to right, var(--scroll-gradient-from), var(--scroll-gradient-to));
    --scroll-shadow-right: linear-gradient(to left, var(--scroll-gradient-from), var(--scroll-gradient-to));
  }
`;

const ShadowScrollArea = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;

  /* Scrollbar */
  --scrollbarBG: transparent;
  --thumbBG: ${theme`colors.green.DEFAULT`};

  scrollbar-width: thin;
  scrollbar-color: var(--thumbBG) var(--scrollbarBG);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: var(--scrollbarBG);
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--thumbBG);
    border-radius: 6px;
    border: 3px solid var(--scrollbarBG);
  }
  /* End scrollbar */
`;

export const Middle: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => (
  <ShadowContainer {...rest}>
    <scroll-shadow>
      <ShadowScrollArea>{children}</ShadowScrollArea>
    </scroll-shadow>
  </ShadowContainer>
);

export const Bottom = styled.div`
  flex-shrink: 0;
  background-color: #313d53;
`;

export const IconContainer = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  user-select: none;

  svg {
    width: 15px;
  }
`;

export interface AssignmentLegacyProps {
  mode: Mode;
  teamSlug: string;
  track: NonNullable<LearnEnvironmentLegacyQuery['track']>;
  challenges: ProgressProps['challenges'];
  challenge: NonNullable<LearnEnvironmentQuery['challenge']>;
  isChecking: boolean;
  completed: boolean;
  hasNextChallenge?: boolean;
  stopTrackCalled: boolean;
  skippingEnabled: boolean;
  toggleNotes(): void;
  onCheckChallenge(): void;
  onNext(): void;
  onExpire(): void;
  onSkip(): void;
  collapse(): void;
  trackReviewsEnabled?: boolean;
}

export function AssignmentLegacy({
  mode,
  teamSlug,
  track,
  challenges,
  challenge,
  onCheckChallenge,
  isChecking,
  toggleNotes,
  completed,
  hasNextChallenge,
  onNext,
  onExpire,
  stopTrackCalled,
  skippingEnabled,
  onSkip,
  collapse,
  trackReviewsEnabled,
}: AssignmentLegacyProps) {
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const history = useHistory();
  const { inviteID } = useParams<{ inviteID?: string }>();

  const scrollLastPanelIntoView = useCallback((lastPanelRef: RefObject<HTMLDivElement>) => {
    // timeout hack due to panel's height animation
    window.setTimeout(() => {
      const scrollableParentElement = getScrollableParent(lastPanelRef.current?.parentElement ?? null);

      if (!lastPanelRef.current || !scrollableParentElement) {
        return;
      }

      const topOffsetPx = 50;
      const cardTopPositionPx = lastPanelRef.current.offsetTop + topOffsetPx;
      const scrollableElementBottomPosition = scrollableParentElement.scrollTop + scrollableParentElement.clientHeight;

      if (cardTopPositionPx > scrollableElementBottomPosition) {
        const scrollOffsetPx = 300;

        // `scrollIntoView` doesn't provide offset option and scrolls the whole element into the view
        // leaving all content above out of the viewport
        scrollableParentElement.scroll({
          top: scrollableParentElement.scrollTop + scrollOffsetPx,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, []);

  const onAccordionToggle: AssignmentMarkdownProps['onToggle'] = (isOpen, sections, section, lastPanelRef) => {
    const index = sections.indexOf(section);

    if (isOpen && index === sections.length - 1) {
      lastPanelRef && scrollLastPanelIntoView(lastPanelRef);
    }

    const accordionNumber = index + 1;
    const accordionCount = sections.length;
    const accordionNumberPct = accordionNumber / accordionCount;

    trackAmplitudeEvent({
      name: 'Lab - Challenge - Toggled accordion',
      data: {
        action: isOpen ? 'expand' : 'collapse',
        accordionNumber,
        accordionCount,
        accordionNumberPct,
      },
    });
  };

  const onClickNotesIcon = () => {
    toggleNotes();
    trackAmplitudeEvent({ name: 'Lab - Challenge - Opened notes' });
  };

  const onClickCompletionBanner = () => {
    trackAmplitudeEvent({ name: 'Lab - Challenge - Clicked correct panel' });
    onNext();
  };

  const onClickCollapseSidebar = () => {
    trackAmplitudeEvent({ name: 'Lab - Challenge - Toggled sidebar', data: { action: 'closed' } });
    collapse();
  };

  const closeToPath = track?.permalink ? addInvitePrefix(track.permalink, inviteID) : undefined;

  const challengeCompleted = challenge.status === ChallengeStatus.Completed;
  const canSkip = skippingEnabled && !challengeCompleted;

  return (
    <>
      <Top>
        <TopContent>
          <IconContainer onClick={onClickCollapseSidebar}>
            <CollapseIcon />
          </IconContainer>

          {!!challenge?.notes?.length && (
            <IconContainer onClick={onClickNotesIcon} aria-label="notes" tw="ml-2">
              <NotesIcon tw="w-2" />
            </IconContainer>
          )}

          <div tw="flex-1 flex flex-row justify-end gap-2 ml-2">
            <Progress
              teamSlug={teamSlug}
              challenges={challenges}
              currentChallenge={challenge}
              mode={mode}
              track={track}
              skippingEnabled={skippingEnabled}
              tw="flex-1"
              trackReviewsEnabled={trackReviewsEnabled}
              challengeTimer={<ChallengeTimer tw="ml-1" track={track} onExpire={onExpire} />}
            />
          </div>
        </TopContent>
      </Top>
      <Middle>
        <div tw="bg-[#3b4558]">
          {!stopTrackCalled && (
            <AssignmentMarkdown title={challenge.title} value={challenge.assignment} onToggle={onAccordionToggle} />
          )}
        </div>
      </Middle>
      <Bottom>
        {completed ? (
          <CompletionBanner
            subtitle={
              !hasNextChallenge ? 'You have successfully completed the track!' : 'Loading your next challenge...'
            }
            onComplete={onClickCompletionBanner}
          />
        ) : (
          !stopTrackCalled && (
            <ChallengeActions
              isCompleted={challengeCompleted}
              canCheck={!!challenge.can_check}
              canExit={mode === Mode.Platform}
              canSkip={canSkip}
              exitTo={{ pathname: closeToPath, state: history.location.state }}
              isChecking={isChecking}
              onCheck={onCheckChallenge}
              onNext={onNext}
              onSkip={onSkip}
            />
          )
        )}
      </Bottom>
    </>
  );
}

function getScrollableParent(node: HTMLElement | null): HTMLElement | null {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  }

  return getScrollableParent(node.parentNode as HTMLElement | null);
}
